import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { IGroupPerformanceSummary } from 'core/hooks';
import { KpiCard, KpiGrid } from 'molecules';
import { PATHS } from 'router/config';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../../_detailShared/detail.styled';
import { GroupColors } from './groupColors';

interface IPerformanceCardProps {
  summary?: IGroupPerformanceSummary;
}

const EmptyMark = styled.span`
  color: ${GroupColors.textMuted};
  font-size: 12px;
`;

const MarksCount = styled.span`
  color: ${GroupColors.textMuted};
`;

const fmtMark = (n: number | null | undefined): string =>
  n == null ? '—' : (Math.round((Number(n) || 0) * 100) / 100).toString();

/**
 * PerformanceCard — backed by `groupPerformanceSummary(groupId)`
 * (backend TZ §G4). Shows average mark + top/bottom three students
 * with mark counts and a tone-coded pill. Helpful for the methodist
 * to scan the group at a glance.
 */
export const PerformanceCard = memo(({ summary }: IPerformanceCardProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  if (!summary) {
    return (
      <S.Card>
        <S.CardTitle>
          {t('pages.groupDetail.performance.cardTitle')}
        </S.CardTitle>
        <S.SubTitle>{t('pages.groupDetail.performance.noData')}</S.SubTitle>
      </S.Card>
    );
  }

  const avg = summary.averageMark;
  const top = (summary.top || []).filter((r): r is NonNullable<typeof r> =>
    Boolean(r),
  );
  const bottom = (summary.bottom || []).filter((r): r is NonNullable<
    typeof r
  > => Boolean(r));

  const renderList = (
    rows: typeof top,
    tone: 'ok' | 'danger',
  ): React.ReactNode =>
    rows.length === 0 ? (
      <EmptyMark>—</EmptyMark>
    ) : (
      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {rows.map((r) => (
          <li
            key={r.studentId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 0',
              fontSize: 12,
            }}
          >
            <L.ChipLink
              type="button"
              onClick={() =>
                history.push(PATHS.studentDetail.replace(':id', r.studentId))
              }
            >
              {r.fullName || '—'}
            </L.ChipLink>
            <L.Tag tone={tone}>{fmtMark(r.averageMark)}</L.Tag>
            <MarksCount>
              {t('pages.groupDetail.performance.marksCount', {
                count: r.marksCount ?? 0,
              })}
            </MarksCount>
          </li>
        ))}
      </ul>
    );

  return (
    <S.Card>
      <S.CardTitle>{t('pages.groupDetail.performance.cardTitle')}</S.CardTitle>
      <div style={{ marginTop: 8 }}>
        <KpiGrid columns={2}>
          <KpiCard
            label={t('pages.groupDetail.performance.averageMark')}
            value={fmtMark(avg)}
            accent={(avg ?? 0) >= 4 ? GroupColors.success : undefined}
          />
          <KpiCard
            label={t('pages.groupDetail.performance.studentsWithoutMarks')}
            value={summary.studentsWithoutMarks ?? 0}
            accent={
              (summary.studentsWithoutMarks ?? 0) > 0
                ? GroupColors.warn
                : undefined
            }
          />
        </KpiGrid>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          marginTop: 12,
        }}
      >
        <div>
          <L.SubblockHeader>
            {t('pages.groupDetail.performance.top')}
          </L.SubblockHeader>
          {renderList(top, 'ok')}
        </div>
        <div>
          <L.SubblockHeader>
            {t('pages.groupDetail.performance.bottom')}
          </L.SubblockHeader>
          {renderList(bottom, 'danger')}
        </div>
      </div>
    </S.Card>
  );
});
